import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, map } from 'rxjs';
import { PlatformAuthModalPanel } from '../enums/platform-auth-modal-panel.enum';
import { PlatformAuthService } from '../services/platform-auth.service';
import { PlatformJobboardLoginModalService } from '../services/platform-jobboard-login-modal.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationGuard {
  private readonly router = inject(Router);
  private readonly auth = inject(PlatformAuthService);
  private readonly authModal = inject(PlatformJobboardLoginModalService);

  /**
   * Protects platform routes and redirects based on the candidate session.
   * @param _next - Activated route snapshot
   * @param state - Router state snapshot
   * @returns Whether navigation is allowed
   */
  canActivate(
    _next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this.auth.ensureSession().pipe(map(() => this.resolve(state)));
  }

  /**
   * Resolves the guard result from the current session and target URL.
   * @param state - Router state snapshot
   * @returns Whether navigation is allowed
   */
  private resolve(state: RouterStateSnapshot): boolean | UrlTree {
    const loggedIn = this.auth.loggedIn();
    const isAuthRoute = state.url.startsWith('/auth');
    const isProtectedRoute =
      state.url.startsWith('/dashboard') ||
      state.url.startsWith('/jobs') ||
      state.url.startsWith('/profile') ||
      state.url.startsWith('/settings') ||
      state.url.startsWith('/applications');

    if (loggedIn && isAuthRoute) {
      return this.router.createUrlTree(['/dashboard']);
    }
    if (!loggedIn && isProtectedRoute) {
      this.authModal.open({
        panel: PlatformAuthModalPanel.LOGIN,
        redirectUrl: state.url,
      });
      return this.router.createUrlTree(['/']);
    }
    return true;
  }
}
